import { isShopify } from '@sparklayer-io/spark-dash-core/constants/platform';
import { createRoute, redirect } from '@tanstack/react-router';
import { loadSessionEnv } from '../../lib/session';
import { lazyPage } from '../common';
import { siteRoute } from '../layouts';

const pages = () => import('./pages');
const onboardingComp = lazyPage(pages, 'ConfigurationOnboardingHost');
const generalComp = lazyPage(pages, 'ConfigurationsGeneralHost');
const quoteStatusesComp = lazyPage(pages, 'QuoteStatusesHost');
const notificationsComp = lazyPage(pages, 'PurchaseNotificationsHost');
const invoicesComp = lazyPage(pages, 'InvoiceSettingsHost');
const storeDetailsComp = lazyPage(pages, 'StoreDetailsHost');
const apiComp = lazyPage(pages, 'APISettingsHost');
const shippingComp = lazyPage(pages, 'ShippingMethodsHost');
const shippingCreateComp = lazyPage(pages, 'ShippingMethodCreateHost');
const shippingEditComp = lazyPage(pages, 'ShippingMethodEditHost');
const themesComp = lazyPage(pages, 'ThemesHost');
const themeEditorComp = lazyPage(pages, 'ThemeEditorHost');

// /configuration redirects to the onboarding page.
const configurationIndexRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration',
  beforeLoad({ params }) {
    throw redirect({ to: '/$siteId/$siteEnv/configuration/onboarding', params });
  },
});

// Shopify stores have their own onboarding flow, so they bounce to the general tab instead.
const configurationOnboardingRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration/onboarding',
  component: onboardingComp,
  staticData: { title: 'Setup - Settings' },
  async beforeLoad({ params }) {
    const env = await loadSessionEnv(params.siteId, params.siteEnv, 'partner');
    if (env && isShopify(env.connected_platform)) {
      throw redirect({ to: '/$siteId/$siteEnv/configuration/general', params });
    }
  },
});

const configurationsGeneralRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration/general',
  component: generalComp,
  staticData: { title: 'Configurations - Settings' },
});

const configurationsQuoteStatusesRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration/general/quote-statuses',
  component: quoteStatusesComp,
  staticData: { title: 'Quote Statuses - Settings' },
});

const configurationsNotificationsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration/general/notifications',
  component: notificationsComp,
  staticData: { title: 'Notifications - Settings' },
});

const configurationsInvoicesRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration/general/invoices',
  component: invoicesComp,
  staticData: { title: 'Invoice Settings - Settings' },
});

const configurationsStoreDetailsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration/general/store-details',
  component: storeDetailsComp,
  staticData: { title: 'Store Details - Settings' },
});

// Legacy URLs kept for existing bookmarks - forward to configuration/general.
const configurationSettingsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration/settings',
  beforeLoad({ params }) {
    throw redirect({ to: '/$siteId/$siteEnv/configuration/general', params });
  },
});

const configurationSettingsGeneralRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration/settings/general',
  beforeLoad({ params }) {
    throw redirect({ to: '/$siteId/$siteEnv/configuration/general', params });
  },
});

const configurationApiRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration/api',
  component: apiComp,
  staticData: { title: 'API - Settings' },
});

const configurationShippingRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration/shipping',
  component: shippingComp,
  staticData: { title: 'Shipping - Settings' },
});

const configurationShippingCreateRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration/shipping/create',
  component: shippingCreateComp,
  staticData: { title: 'Create Shipping Method - Settings' },
});

const configurationShippingEditRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration/shipping/edit/$id',
  component: shippingEditComp,
  staticData: { title: 'Edit Shipping Method - Settings' },
});

const configurationThemeRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration/theme',
  component: themesComp,
  staticData: { title: 'Theme - Settings' },
});

// `{-$slug}` is TanStack's optional-param syntax: the themes table links here without one.
const themeEditorRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration/theme/editor/$themeId/{-$slug}',
  component: themeEditorComp,
  staticData: { title: 'Theme Editor', fullBleed: true, fullHeight: true },
});

// Legacy integration callback URLs (DEV-5499) and the partner-integrations redirects on the
// configuration tree - all forward to the integrations family.
const configurationIntegrationsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration/integrations',
  beforeLoad({ params }) {
    throw redirect({ to: '/$siteId/$siteEnv/integrations/platform', params });
  },
});

const configurationIntegrationsPlatformRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration/integrations/platform',
  beforeLoad({ params }) {
    throw redirect({ to: '/$siteId/$siteEnv/integrations/platform', params });
  },
});

const configurationOAuthBrightpearlRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration/integrations/oauth-brightpearl',
  beforeLoad({ params }) {
    throw redirect({
      to: '/$siteId/$siteEnv/integrations/partners/oauth/brightpearl',
      params,
      search: true,
      hash: true,
    });
  },
});

const configurationOAuthKatanaRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration/integrations/oauth-katana',
  beforeLoad({ params }) {
    throw redirect({
      to: '/$siteId/$siteEnv/integrations/partners/oauth/katana',
      params,
      search: true,
      hash: true,
    });
  },
});

const configurationOAuthQuickbooksRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration/integrations/oauth-quickbooks',
  beforeLoad({ params }) {
    throw redirect({
      to: '/$siteId/$siteEnv/integrations/partners/oauth/quickbooks',
      params,
      search: true,
      hash: true,
    });
  },
});

const configurationOAuthXeroRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'configuration/integrations/oauth-xero',
  beforeLoad({ params }) {
    throw redirect({
      to: '/$siteId/$siteEnv/integrations/partners/oauth/xero',
      params,
      search: true,
      hash: true,
    });
  },
});

/** Configuration family leaves to attach under the site-env layout. */
export const configurationLeaves = [
  configurationIndexRoute,
  configurationOnboardingRoute,
  configurationsGeneralRoute,
  configurationsQuoteStatusesRoute,
  configurationsNotificationsRoute,
  configurationsInvoicesRoute,
  configurationsStoreDetailsRoute,
  configurationSettingsRoute,
  configurationSettingsGeneralRoute,
  configurationApiRoute,
  configurationShippingRoute,
  configurationShippingCreateRoute,
  configurationShippingEditRoute,
  configurationThemeRoute,
  themeEditorRoute,
  configurationIntegrationsRoute,
  configurationIntegrationsPlatformRoute,
  configurationOAuthBrightpearlRoute,
  configurationOAuthKatanaRoute,
  configurationOAuthQuickbooksRoute,
  configurationOAuthXeroRoute,
];
